/**
 * One run: what it did, what it produced, and why it is not downloadable if it is not.
 */

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import type { ValidationResult } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { Console } from "@/components/Console";
import { DownloadAllButton } from "@/components/DownloadAllButton";
import { DownloadButton } from "@/components/DownloadButton";
import { useProject } from "@/components/ProjectLayout";
import {
  Badge,
  ErrorState,
  Loading,
  Notice,
  RelativeTime,
  Status,
  formatBytes,
  formatDuration,
  isTerminal,
} from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";
import { useRunConsole } from "@/hooks/useRunConsole";

function Validations({ results }: { results: ValidationResult[] }) {
  const [open, setOpen] = useState<string>();
  if (results.length === 0) return null;

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Validation</span>
        <span className="muted" style={{ fontSize: 12.5 }}>
          {results.filter((r) => r.blocks_download).length > 0
            ? "A failure at error severity blocks the download"
            : `${results.length} checks ran`}
        </span>
      </div>

      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Check</th>
              <th>Target</th>
              <th>Result</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {results.map((result) => {
              const findings =
                (result.details as { findings?: Array<{ location: string; message: string; value?: string }> } | null)
                  ?.findings ?? [];
              const expanded = open === result.id;

              return (
                <tr key={result.id}>
                  <td>
                    <div style={{ fontWeight: 550 }}>{result.validator_name ?? result.validator_type}</div>
                    <div className="faint mono" style={{ fontSize: 11.5 }}>
                      {result.validator_type}
                    </div>
                  </td>
                  <td className="mono muted">{result.target}</td>
                  <td>
                    {result.status === "passed" ? (
                      <Badge tone="success">Passed</Badge>
                    ) : result.severity === "warning" ? (
                      <Badge tone="warning">Warning</Badge>
                    ) : result.status === "skipped" ? (
                      <Badge>Skipped</Badge>
                    ) : (
                      <Badge tone="danger">
                        {result.status === "errored" ? "Could not run" : "Failed"}
                      </Badge>
                    )}
                  </td>
                  <td>
                    <div className="muted">{result.message}</div>
                    {findings.length > 0 && (
                      <>
                        <button
                          type="button"
                          className="button button--small button--ghost"
                          style={{ padding: "2px 0" }}
                          onClick={() => setOpen(expanded ? undefined : result.id)}
                        >
                          {expanded ? "Hide" : `Show ${findings.length} findings`}
                        </button>
                        {expanded && (
                          <ul className="mono" style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                            {findings.map((finding, index) => (
                              <li key={index} style={{ marginBottom: 3 }}>
                                <strong>{finding.location}</strong> — {finding.message}
                                {finding.value && <span className="faint"> ({finding.value})</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function RunDetail() {
  const { runId = "" } = useParams();
  const { project, can } = useProject();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  const { data, error, loading, reload } = useAsync(
    () => endpoints.run(project.slug, runId),
    [project.slug, runId],
    // Polled only while it is still going; a finished run never changes.
    { pollMs: 5_000 },
  );

  const live = data ? !isTerminal(data.status) : false;
  const consoleState = useRunConsole(project.slug, runId, live);

  if (loading && !data) return <Loading rows={5} label="Loading run" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return null;

  // Both default to empty server-side, but the schema marks them optional.
  const artifacts = data.artifacts ?? [];
  const validations = data.validations ?? [];
  const outputs = artifacts.filter((a) => a.kind === "output");
  const inputs = artifacts.filter((a) => a.kind === "input");
  const blocked = validations.some((v) => v.blocks_download);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setProblem(undefined);
    try {
      await action();
      reload();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="breadcrumbs">
        <Link to={`/projects/${project.slug}/runs`}>Runs</Link>
        <span>/</span>
        <span>#{data.number}</span>
      </div>

      <div className="row row--between">
        <div>
          <div className="row">
            <h2>
              <Link to={`/projects/${project.slug}/scripts/${data.script_name}`}>
                {data.script_name}
              </Link>{" "}
              <span className="muted">#{data.number}</span>
            </h2>
            <Status status={data.status} />
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
            {data.triggered_by_email ?? `Triggered by a ${data.trigger}`} ·{" "}
            <RelativeTime iso={data.created_at} />
            {data.git_sha && <> · <span className="mono">{data.git_sha.slice(0, 7)}</span></>}
          </div>
        </div>

        <div className="row">
          {live && can("pm") && (
            <button
              type="button"
              className="button button--small button--danger"
              disabled={busy}
              onClick={() => act(() => endpoints.cancelRun(project.slug, runId))}
            >
              Cancel run
            </button>
          )}
          {!live && can("pm") && (
            <button
              type="button"
              className="button button--small"
              disabled={busy}
              onClick={() =>
                act(async () => {
                  const created = await endpoints.rerun(project.slug, runId);
                  await endpoints.startRun(project.slug, created.run.id);
                  navigate(`/projects/${project.slug}/runs/${created.run.id}`);
                })
              }
            >
              Run again
            </button>
          )}
        </div>
      </div>

      {problem && <Notice tone="danger">{problem}</Notice>}

      {data.error_message && (
        <Notice tone={data.status === "cancelled" ? "info" : "danger"} title="What happened">
          {data.error_message}
        </Notice>
      )}

      {data.skip_reason && (
        <Notice tone="info" title="This run was skipped">
          {data.skip_reason}
        </Notice>
      )}

      <div className="row" style={{ gap: 24, fontSize: 13 }}>
        <span className="muted">
          Duration <strong className="mono">{formatDuration(data.duration_ms)}</strong>
        </span>
        {data.exit_code !== null && (
          <span className="muted">
            Exit code <strong className="mono">{data.exit_code}</strong>
          </span>
        )}
        <span className="muted">
          Started <RelativeTime iso={data.started_at} />
        </span>
      </div>

      <Console
        lines={consoleState.lines}
        streaming={consoleState.streaming}
        complete={consoleState.complete}
        error={consoleState.error}
      />

      <Validations results={validations} />

      {blocked && (
        <Notice tone="danger" title="This output failed validation">
          It is not available for download. A project administrator can release it
          deliberately, and that decision is recorded.
        </Notice>
      )}

      {(outputs.length > 0 || inputs.length > 0) && (
        <div className="card">
          <div className="card__header">
            <span className="card__title">Files</span>
          </div>

          {outputs.length > 0 && (
            <>
              <div className="card__header" style={{ borderTop: "1px solid var(--border)", paddingTop: 10, paddingBottom: 10 }}>
                <span className="muted" style={{ fontSize: 13, fontWeight: 550 }}>Outputs</span>
                {outputs.filter((a) => !a.purged_at).length > 1 && (
                  <DownloadAllButton
                    projectSlug={project.slug}
                    runId={data.id}
                    fileCount={outputs.filter((a) => !a.purged_at).length}
                    totalBytes={outputs
                      .filter((a) => !a.purged_at)
                      .reduce((sum, a) => sum + (a.size_bytes ?? 0), 0)}
                    needsReason={project.is_pii || blocked}
                    kind="output"
                    onBundled={reload}
                  />
                )}
              </div>
              <div className="table__scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Size</th>
                      <th>Checksum</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {outputs.map((artifact) => (
                      <tr key={artifact.id}>
                        <td className="mono">{artifact.filename}</td>
                        <td className="muted">{formatBytes(artifact.size_bytes)}</td>
                        <td className="faint mono truncate" style={{ maxWidth: 120 }}>
                          {artifact.checksum_sha256?.slice(0, 12) ?? "—"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <DownloadButton
                            projectSlug={project.slug}
                            runId={runId}
                            artifact={artifact}
                            needsReason={project.is_pii}
                            blocked={blocked}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {inputs.length > 0 && (
            <>
              <div className="card__header" style={{ borderTop: "1px solid var(--border)", paddingTop: 10, paddingBottom: 10 }}>
                <span className="muted" style={{ fontSize: 13, fontWeight: 550 }}>Inputs</span>
                {inputs.filter((a) => !a.purged_at).length > 1 && (
                  <DownloadAllButton
                    projectSlug={project.slug}
                    runId={data.id}
                    fileCount={inputs.filter((a) => !a.purged_at).length}
                    totalBytes={inputs
                      .filter((a) => !a.purged_at)
                      .reduce((sum, a) => sum + (a.size_bytes ?? 0), 0)}
                    needsReason={project.is_pii}
                    kind="input"
                    onBundled={reload}
                  />
                )}
              </div>
              <div className="table__scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Size</th>
                      <th>Checksum</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {inputs.map((artifact) => (
                      <tr key={artifact.id}>
                        <td className="mono">{artifact.filename}</td>
                        <td className="muted">{formatBytes(artifact.size_bytes)}</td>
                        <td className="faint mono truncate" style={{ maxWidth: 120 }}>
                          {artifact.checksum_sha256?.slice(0, 12) ?? "—"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <DownloadButton
                            projectSlug={project.slug}
                            runId={runId}
                            artifact={artifact}
                            needsReason={project.is_pii}
                            blocked={false}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {data.argv && (
        <details className="card">
          <summary style={{ padding: "13px 18px", cursor: "pointer", fontWeight: 550, fontSize: 14 }}>
            Exact command
          </summary>
          <div className="card__body">
            <pre className="mono" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
              {data.argv.join(" ")}
            </pre>
            <p className="faint" style={{ marginTop: 10, marginBottom: 0, fontSize: 12 }}>
              Safe to share: secrets are passed as environment variables, never as
              arguments.
            </p>
          </div>
        </details>
      )}
    </div>
  );
}
